import { type ReactElement } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { useFeature } from '@/features/feature-flags/useFeature';
import type { SensitivityViewModel } from '@/models/patient';

interface BreakGlassBadgeProps {
  readonly sensitivity: SensitivityViewModel;
}

/**
 * Persistent Break-Glass indicator rendered in the PatientIdentityBand's
 * statusSlot once the acknowledgement gate has been cleared. Keeps
 * clinicians visually aware that they're inside a sensitive record while
 * they review the data.
 *
 * Hidden when `breakGlass.enabled` is off (defense-in-depth alongside
 * FE-S4) or when the record isn't flagged.
 */
export function BreakGlassBadge({
  sensitivity,
}: BreakGlassBadgeProps): ReactElement | null {
  const { t } = useTranslation();
  const enabled = useFeature('breakGlass.enabled') === true;
  if (!enabled || !sensitivity.flagged) return null;
  return (
    <Badge
      variant="destructive"
      className="inline-flex items-center gap-1 h-5 shrink-0"
      title={sensitivity.reason ?? undefined}
    >
      <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
      {t('patient.breakGlass.badge')}
    </Badge>
  );
}
